/**
 * The public id of a cubic coding-agent run, as GitHub comments, check runs and `/code/fix/<id>`
 * carry it, and as `FixSession.sandboxId` keys its record. Client-safe.
 *
 * A run on the legacy CLI pipeline is named by its sandbox, `vercel-<uuid>`. A run on code
 * sessions is a turn of its pull request's cubic code session and is named `code-<turnUuid>`,
 * where `turnUuid` is its `CodeSessionTurn.id`. This module owns that format: build and parse it
 * only through these functions.
 */

/** What a run on code sessions starts with. */
export const CODE_SESSION_RUN_ID_PREFIX = 'code-';

/** What a legacy CLI run's sandbox id starts with. */
const LEGACY_RUN_ID_PREFIX = 'vercel-';

const UUID_PATTERN = /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i;

/** The run id of a run on code sessions: its turn's id behind `code-`, lowercased. */
export function buildCodeSessionRunId(turnId: string): string {
  if (!UUID_PATTERN.test(turnId)) {
    throw new Error("A code session run id needs its turn's UUID");
  }
  return `${CODE_SESSION_RUN_ID_PREFIX}${turnId.toLowerCase()}`;
}

/**
 * The turn id, lowercased, of a run on code sessions; null for anything that is not
 * `code-<uuid>`, a legacy sandbox id included.
 */
export function parseCodeSessionRunId(runId: string): string | null {
  if (!runId.startsWith(CODE_SESSION_RUN_ID_PREFIX)) {
    return null;
  }
  const turnId = runId.slice(CODE_SESSION_RUN_ID_PREFIX.length);
  return UUID_PATTERN.test(turnId) ? turnId.toLowerCase() : null;
}

/** Whether the id names a legacy CLI run's sandbox (`vercel-…`). */
export function isLegacyRunId(runId: string): boolean {
  return runId.startsWith(LEGACY_RUN_ID_PREFIX) && runId.length > LEGACY_RUN_ID_PREFIX.length;
}
